import { useWorkspaceAccess, type WorkspaceAccessPayload } from "@/features/workspaces/api";
import type { WorkspaceRow, WorkspaceSettingsRow, WorkspaceRole } from "@bizy/core";
import { createContext, useContext, useMemo, type ReactNode } from "react";

export interface WorkspaceContextValue {
  workspaceId: string;
  workspace: WorkspaceRow;
  settings: WorkspaceSettingsRow;
  role: WorkspaceRole;
  scopes: readonly string[];
  isSuperuser: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider = ({
  workspaceId,
  children,
}: {
  workspaceId: string;
  children: ReactNode;
}) => {
  const accessQuery = useWorkspaceAccess(workspaceId);
  const payload: WorkspaceAccessPayload | undefined = accessQuery.data;

  const value = useMemo<WorkspaceContextValue | null>(() => {
    if (!payload) return null;
    return {
      workspaceId,
      workspace: payload.workspace,
      settings: payload.settings,
      role: payload.role,
      scopes: payload.scopes,
      isSuperuser: payload.is_superuser,
    };
  }, [payload, workspaceId]);

  if (!value) return null;

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = (): WorkspaceContextValue => {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace must be used inside WorkspaceProvider.");
  return context;
};
